import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../../utils/api";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { resolveProductPricing } from "../../../utils/productPricing";
import {
  INITIAL_CUSTOMER_STATE,
  API_ENDPOINTS,
  MESSAGES,
  VALIDATION,
} from "../constants";
import { buildOrderPayload } from "../utils";

/**
 * Custom hook for managing takeaway order logic
 * Handles customer info, products, calculations, and order submission
 */
export const useTakeawayOrder = () => {
  const [loading, setLoading] = useState(false);
  const { restaurantId, gstFraction } = useRestaurantConfig();
  const gstPercent = gstFraction * 100;

  const [customer, setCustomer] = useState(INITIAL_CUSTOMER_STATE);
  const [products, setProducts] = useState([]);
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);

  /**
   * Once the phone number is fully typed (10 digits), look up this
   * customer's past orders and auto-fill name/address from their most
   * recent order - only into fields that are still empty, so it never
   * stomps on something the cashier already typed.
   */
  const lastLookedUpPhone = useRef("");
  useEffect(() => {
    const phone = customer.phone;
    if (phone.length !== VALIDATION.MAX_PHONE_LENGTH) {
      // Not a complete number right now - let a future completion
      // (even of this same number, e.g. after Cancel resets the form)
      // trigger a fresh lookup.
      lastLookedUpPhone.current = "";
      return;
    }
    if (phone === lastLookedUpPhone.current) return;
    lastLookedUpPhone.current = phone;

    let cancelled = false;
    (async () => {
      setLookingUpCustomer(true);
      try {
        const res = await api.get(`${API_ENDPOINTS.ORDERS_BY_PHONE}/${phone}`);
        if (cancelled) return;

        const orders = res.data || [];
        if (orders.length === 0) return;

        const latest = [...orders].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        )[0];

        let filledSomething = false;
        setCustomer((prev) => {
          // Re-check phone/emptiness against the latest state, not the
          // value captured when this lookup started.
          if (prev.phone !== phone) return prev;
          const next = { ...prev };
          if (!next.name.trim() && latest.name) {
            next.name = latest.name;
            filledSomething = true;
          }
          if (!next.address.trim() && latest.address) {
            next.address = latest.address;
            filledSomething = true;
          }
          return next;
        });

        if (filledSomething) {
          toast.info(MESSAGES.CUSTOMER_HISTORY_FOUND);
        }
      } catch (err) {
        console.error("Customer lookup by phone failed", err);
      } finally {
        if (!cancelled) setLookingUpCustomer(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customer.phone]);

  /**
   * Add product to order or increase quantity if already exists
   */
  const handleAddProduct = (prod, qty = 1) => {
    const exists = products.find((p) => p._id === prod._id);

    if (exists) {
      setProducts((prev) =>
        prev.map((p) =>
          p._id === prod._id ? { ...p, quantity: p.quantity + qty } : p,
        ),
      );
      toast.success(MESSAGES.QUANTITY_INCREASED(prod.productName));
    } else {
      const { price, offerPrice, effectivePrice, packageCharge } =
        resolveProductPricing(prod);
      const addOns = prod.addOns || [];
      const addOnsTotal = addOns.reduce(
        (s, a) => s + Number(a.price || 0) * Number(a.quantity || 1),
        0,
      );
      setProducts([
        ...products,
        {
          ...prod,
          quantity: qty,
          price,
          offerPrice,
          packageCharge,
          finalPrice: effectivePrice + addOnsTotal,
          addOns,
          imageUrl: prod.imageUrl || prod.image || "",
          // isTaxable: prod.isTaxable === true,
        },
      ]);
      toast.success(MESSAGES.PRODUCT_ADDED(prod.productName));
    }
  };

  /**
   * Update product quantity
   */
  const updateQuantity = (id, qty) => {
    setProducts((prev) =>
      prev.map((p) => (p._id === id ? { ...p, quantity: qty } : p)),
    );
  };

  /**
   * Update a product's note (special instructions, e.g. "no onions")
   */
  const updateProductNote = (id, note) => {
    setProducts((prev) =>
      prev.map((p) => (p._id === id ? { ...p, note } : p)),
    );
  };

  /**
   * Remove product from order
   */
  const removeProduct = (id) => {
    setProducts((prev) => prev.filter((p) => p._id !== id));
    toast.info(MESSAGES.PRODUCT_REMOVED);
  };

  /**
   * Change which add-ons are attached to an already-added product,
   * recomputing finalPrice to reflect the new selection.
   */
  const updateProductAddOns = (id, chosenAddOns, availableAddOns) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p._id !== id) return p;
        const oldAddOnsTotal = (p.addOns || []).reduce(
          (s, a) => s + Number(a.price || 0) * Number(a.quantity || 1),
          0,
        );
        const newAddOnsTotal = chosenAddOns.reduce(
          (s, a) => s + Number(a.price || 0) * Number(a.quantity || 1),
          0,
        );
        const basePrice = Number(p.finalPrice || 0) - oldAddOnsTotal;
        return {
          ...p,
          addOns: chosenAddOns,
          finalPrice: basePrice + newAddOnsTotal,
          availableAddOns: availableAddOns ?? p.availableAddOns,
        };
      }),
    );
  };

  /**
   * Calculate order totals
   */
  const isDineIn = customer.orderType === "dinein";

  const calculations = {
    subtotal: products.reduce((sum, p) => {
      const price = Number(p.finalPrice || p.offerPrice || p.price || 0);
      return sum + p.quantity * price;
    }, 0),

    totalPackageCharge: isDineIn
      ? 0
      : products.reduce((sum, p) => {
        const charge = Number(p.packageCharge || 0);
        return sum + p.quantity * charge;
      }, 0),

    get taxableSubtotal() {
      return products.reduce((sum, p) => {
        if (p.isTaxable !== true) return sum;

        const price = Number(p.finalPrice || p.offerPrice || p.price || 0);
        return sum + p.quantity * price;
      }, 0);
    },

    get gstAmount() {
      return this.taxableSubtotal * gstFraction;
    },

    get grandTotal() {
      return this.subtotal + this.gstAmount + this.totalPackageCharge;
    },
  };

  /**
   * Clear all order data
   */
  const handleCancel = () => {
    setCustomer(INITIAL_CUSTOMER_STATE);
    setProducts([]);
    toast.info(MESSAGES.ORDER_CLEARED);
  };

  /**
   * Validate and submit order
   */
  const handleSubmit = async () => {
    // Validation
    if (products.length === 0) {
      toast.error(MESSAGES.NO_PRODUCTS);
      return;
    }

    if (products.some((p) => p.quantity <= 0)) {
      toast.error(MESSAGES.INVALID_QUANTITY);
      return;
    }

    // if (!customer.name.trim()) {
    //   toast.error(MESSAGES.NAME_REQUIRED);
    //   return;
    // }

    // if (!customer.phone.trim()) {
    //   toast.error(MESSAGES.PHONE_REQUIRED);
    //   return;
    // }

    // Build payload
    const payload = buildOrderPayload({
      customer,
      products,
      calculations,
      restaurantId,
    });

    // Submit order
    setLoading(true);
    try {
      const response = await api.post(API_ENDPOINTS.CREATE_ORDER, payload);

      if (response.status === 201) {
        // Kitchen waiting time, to tell the customer
        let waitText = "";
        try {
          const orderId = response.data?.orderId;
          const { data } = await api.get("/kitchen/eta", { params: { orderIds: orderId } });
          const eta = data?.orders?.[orderId];
          if (eta?.waitMin > 0) waitText = ` Ready in about ${eta.waitMin} min.`;
        } catch {
          // no estimate: just the usual message
        }
        toast.success(`${MESSAGES.ORDER_SUCCESS}${waitText}`, { autoClose: waitText ? 5000 : 3000 });
        setTimeout(() => {
          window.location.reload();
        }, waitText ? 5000 : 2000);
      }
    } catch (error) {
      console.error("Error creating order:", error.response?.data);
      toast.error(error.response?.data?.message || MESSAGES.ORDER_FAILED);
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    loading,
    customer,
    setCustomer,
    products,
    gstPercent,
    lookingUpCustomer,

    // Calculations
    ...calculations,

    // Handlers
    handleAddProduct,
    updateQuantity,
    removeProduct,
    updateProductAddOns,
    updateProductNote,
    handleCancel,
    handleSubmit,

    // Validation
    isFormValid: products.length > 0,
  };
};
